export interface ChatChartDataPoint {
  label: string;
  value: number;
}

export interface ChatChartSpec {
  type: 'bar' | 'line' | 'pie' | 'area' | string;
  title: string;
  description?: string;
  xAxis?: string;
  yAxis?: string;
  data: ChatChartDataPoint[];
}

const CHART_PALETTE = [
  '#027c06',
  '#1a73e8',
  '#fbbc04',
  '#a855f7',
  '#ea4335',
  '#0b8793',
  '#f97316',
  '#6366f1',
];

// PepsiCo number-formatting standard the backend already applies to its
// narrative/table text (Thousands -> M, Millions -> MM, Billions -> B,
// Trillions -> T), so chart axis/tooltip labels read the same way instead of
// falling back to generic SI "K"/"M" abbreviations.
const formatCompactValue = (value: number): string => {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1e12) return `${sign}${(abs / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}MM`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}M`;
  if (abs >= 100) return `${sign}${abs.toFixed(0)}`;
  if (abs >= 10) return `${sign}${abs.toFixed(1)}`;
  return `${sign}${abs.toFixed(2).replace(/\.00$/, '')}`;
};

const isNonCurrencyAxis = (label?: string): boolean =>
  Boolean(label && /count|number of|qty|quantity|%|percent|score|rate\b/i.test(label));

// The backend sometimes pre-scales chart data points (e.g. a title of
// "... ($MM)" means each value is already expressed in millions) rather than
// always sending raw dollar amounts.
const getChartUnitScale = (chart: ChatChartSpec): number => {
  const text = `${chart.title} ${chart.yAxis ?? ''} ${chart.description ?? ''}`;
  if (/\bmillions?\b|\bmm\b/i.test(text)) return 1e6;
  if (/\bbillions?\b/i.test(text)) return 1e9;
  if (/\bthousands?\b/i.test(text)) return 1e3;
  return 1;
};

const formatChartValue = (value: number, yAxis?: string): string =>
  isNonCurrencyAxis(yAxis) ? formatCompactValue(value) : `$${formatCompactValue(value)}`;

export function buildChartOption(chart: ChatChartSpec): Record<string, unknown> {
  const isPie = chart.type === 'pie';
  const isLineLike = chart.type === 'line' || chart.type === 'area';
  const scale = getChartUnitScale(chart);
  const fmt = (value: number) => formatChartValue(value * scale, chart.yAxis);
  const points = Array.isArray(chart.data) ? chart.data : [];

  if (isPie) {
    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(32,33,36,0.95)',
        borderWidth: 0,
        textStyle: {color: '#fff'},
        formatter: (params: any) => `${params.name}<br/><strong>${fmt(params.value)}</strong> (${params.percent}%)`,
      },
      legend: {bottom: 0, textStyle: {color: '#5f6368', fontSize: 11}},
      series: [
        {
          type: 'pie',
          radius: ['38%', '68%'],
          center: ['50%', '44%'],
          itemStyle: {borderColor: '#fff', borderWidth: 2},
          label: {
            formatter: (params: any) => fmt(params.value),
            fontSize: 11,
            color: '#202124',
          },
          data: points.map((d, i) => ({
            name: d.label,
            value: d.value,
            itemStyle: {color: CHART_PALETTE[i % CHART_PALETTE.length]},
          })),
        },
      ],
    };
  }

  return {
    grid: {left: 18, right: 18, top: 16, bottom: 64, containLabel: true},
    xAxis: {
      type: 'category',
      data: points.map((d) => d.label),
      name: chart.xAxis ?? '',
      nameLocation: 'middle',
      nameGap: 44,
      nameTextStyle: {color: '#5f6368', fontSize: 11, fontWeight: 600},
      axisLabel: {
        color: '#5f6368',
        fontSize: 11,
        rotate: points.length > 5 ? 24 : 0,
        interval: 0,
      },
      axisLine: {lineStyle: {color: '#dadce0'}},
      boundaryGap: !isLineLike,
    },
    yAxis: {
      type: 'value',
      name: chart.yAxis ?? '',
      nameTextStyle: {color: '#5f6368', fontSize: 11},
      axisLabel: {
        color: '#5f6368',
        fontSize: 11,
        formatter: (value: number) => fmt(value),
      },
      splitLine: {lineStyle: {color: '#e8eaed', type: 'dashed'}},
    },
    series: [
      isLineLike
        ? {
            type: 'line',
            smooth: true,
            symbolSize: 7,
            lineStyle: {width: 3, color: '#027c06'},
            itemStyle: {color: '#027c06'},
            areaStyle: chart.type === 'area' ? {color: 'rgba(2,124,6,0.12)'} : undefined,
            data: points.map((d) => d.value),
            label: {
              show: true,
              position: 'top',
              formatter: (params: any) => fmt(params.value),
              fontSize: 11,
              color: '#202124',
            },
          }
        : {
            type: 'bar',
            data: points.map((d) => ({
              value: d.value,
              itemStyle: {color: d.value >= 0 ? '#027c06' : '#d93025', borderRadius: [8, 8, 0, 0]},
            })),
            barMaxWidth: 56,
            label: {
              show: true,
              position: 'top',
              formatter: (params: any) => fmt(params.value),
              fontSize: 11,
              color: '#202124',
            },
          },
    ],
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(32,33,36,0.95)',
      borderWidth: 0,
      textStyle: {color: '#fff'},
      formatter: (params: any) => {
        const point = Array.isArray(params) ? params[0] : params;
        return `${point.axisValueLabel}<br/><strong>${fmt(point.value)}</strong>`;
      },
    },
  };
}
